import { Sentry } from "./sentry";
import { useAuthStore } from "@/context/auth/AuthContext";

type AuthUser = {
  id?: string | number | null;
  email?: string | null;
  type?: string | null;
  businessId?: string | null;
  name?: string | null;
  lastName?: string | null;
};

/**
 * Aplica identidad (user + tags) al scope global de Sentry. Pasar null/undefined
 * limpia todo. Idempotente: llamarla con el mismo payload no produce eventos.
 */
export function identifyUser(user: AuthUser | null | undefined): void {
  if (!user) {
    clearIdentity();
    return;
  }

  Sentry.setUser({
    id: user.id != null ? String(user.id) : undefined,
    email: user.email ?? undefined,
    username:
      [user.name, user.lastName].filter(Boolean).join(" ").trim() || undefined,
  });

  // `type` es el nombre interno del campo en Trud (owner/employee/etc.);
  // se expone en Sentry como `role` que es la convención más universal.
  Sentry.setTag("role", user.type ?? null);
  Sentry.setTag("businessId", user.businessId ?? null);
}

export function clearIdentity(): void {
  Sentry.setUser(null);
  Sentry.setTag("role", null);
  Sentry.setTag("businessId", null);
}

/**
 * Conecta el store de auth a Sentry: identifica al usuario actual (si ya
 * está hidratado de localStorage) y reacciona a cualquier cambio futuro
 * (login, logout, refresh /me). Devuelve la función unsubscribe por si
 * algún test la necesita.
 */
export function subscribeAuthIdentity(): () => void {
  const initial = useAuthStore.getState() as { user: AuthUser | null };
  identifyUser(initial.user);

  return useAuthStore.subscribe((state, prevState) => {
    const s = state as { user: AuthUser | null };
    const p = prevState as { user: AuthUser | null };
    if (s.user !== p.user) {
      identifyUser(s.user);
    }
  });
}
